import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

type InvalidateTagsArg = ReadonlyArray<
  string | { type: string; id?: string | number }
>;
type ApiSliceWithInvalidate = {
  util: {
    invalidateTags: (...args: any[]) => unknown;
  };
};
interface IRefreshInvalidationTarget {
  apiSlice: ApiSliceWithInvalidate;
  tags: InvalidateTagsArg;
}

interface IUsePullRefreshInvalidateTagsOptions {
  hideDuration?: number;
  hideDurationMs?: number;
  onBeforeInvalidate?: () => void | Promise<void>;
  onAfterInvalidate?: () => void | Promise<void>;
}

export function usePullRefreshInvalidateTags(
  invalidationTargets: ReadonlyArray<IRefreshInvalidationTarget>,
  options?: IUsePullRefreshInvalidateTagsOptions,
) {
  const dispatch = useDispatch();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const inFlightRefreshRef = useRef<Promise<void> | null>(null);
  const hideDurationMs =
    options?.hideDuration ?? options?.hideDurationMs ?? 100;

  const onRefresh = useCallback(() => {
    if (inFlightRefreshRef.current) {
      return inFlightRefreshRef.current;
    }

    const refreshPromise = (async () => {
      setIsRefreshing(true);
      try {
        await options?.onBeforeInvalidate?.();
        invalidationTargets.forEach(({ apiSlice, tags }) => {
          dispatch(apiSlice.util.invalidateTags([...tags]) as any);
        });
        await options?.onAfterInvalidate?.();
        if (hideDurationMs > 0) {
          await new Promise((resolve) =>
            window.setTimeout(resolve, hideDurationMs),
          );
        }
      } finally {
        setIsRefreshing(false);
        inFlightRefreshRef.current = null;
      }
    })();

    inFlightRefreshRef.current = refreshPromise;
    return refreshPromise;
  }, [dispatch, hideDurationMs, invalidationTargets, options]);

  return {
    isRefreshing,
    onRefresh,
  };
}
